/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getLocalizedPath, isQueryParamLocaleMode } from "../utils/locale-path.utils";

import { buildLocalizedUrl } from "./url.utils";

import type { LocalePrefixMode } from "../types/locale.types";

export interface AlternatesProps {
	canonical: string;
	languages: { [key: string]: string };
}

export const getAlternates = (
	locale: string,
	slug: string,
	locales?: string[],
	defaultLocale?: string,
	localePrefix?: LocalePrefixMode,
): AlternatesProps => {
	const { fullUrl } = buildLocalizedUrl(locale, slug);
	const languages: { [key: string]: string } = {};

	if (locales?.length) {
		const normalizedSlug = slug === "/home" ? "" : slug;
		const baseUrl = process.env.EPIC_SERVER_URL || "";
		const effectiveDefault = defaultLocale || locale;

		if (isQueryParamLocaleMode() && baseUrl) {
			// Query-param mode: use buildLocalizedUrl (handles ?lang=)
			for (const loc of locales) {
				languages[loc] = buildLocalizedUrl(loc, slug).fullUrl;
			}
			// x-default: URL without lang param (matches sitemap.utils.ts behavior)
			languages["x-default"] = `${baseUrl}${normalizedSlug || "/"}`;
		} else {
			// Path-based mode: use getLocalizedPath (handles "always" + "as-needed")
			// Returns relative paths — Next.js metadataBase resolves to full URLs
			const prefix = localePrefix || "always";
			for (const loc of locales) {
				languages[loc] = getLocalizedPath(normalizedSlug || "/", loc, effectiveDefault, prefix);
			}
			// x-default: default locale URL
			languages["x-default"] = getLocalizedPath(normalizedSlug || "/", effectiveDefault, effectiveDefault, prefix);
		}
	}

	return { canonical: fullUrl, languages };
};
